import { useState } from "react";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChipGroup, Field, CheckRow } from "@/components/site/FormBits";
import { AddOnPicker } from "@/components/site/AddOnPicker";
import { BookCallStrip } from "@/components/site/BookCall";
import { api, formatApiError } from "@/lib/api";
import { CONTACT_PREFS } from "@/lib/constants";

export const AI_COURSE = "AI Website Web Developer Course";
const LEVELS = ["Complete beginner", "Some website experience", "Freelancer / designer", "Business owner", "Developer"];

export function AiCourseLeadForm() {
  const [f, setF] = useState({ journey_stage: "", goals: "", name: "", email: "", whatsapp: "", whatsapp_consent: false, contact_pref: "Email", add_ons: [], add_ons_other: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.journey_stage) return toast.error("Please choose your experience level");
    setBusy(true);
    try {
      await api.post("/leads", { ...f, interest: AI_COURSE });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="text-center py-10" data-testid="ai-lead-success">
        <CheckCircle2 className="h-12 w-12 text-[#1A9AA8] mx-auto" />
        <h3 className="mt-4 text-2xl font-bold text-[#0B2A4A]">Thanks — your request is in.</h3>
        <p className="mt-3 text-slate-600">Mr. Greg will send you more information about the {AI_COURSE} by your preferred contact method.</p>
      </div>
    );
  return (
    <form onSubmit={submit} className="flex flex-col gap-6" data-testid="ai-lead-form">
      <BookCallStrip source="ai-lead-form" />
      <Field label="Your experience level">
        <ChipGroup options={LEVELS} value={f.journey_stage} onChange={set("journey_stage")} multi={false} testId="ai-lead-level" />
      </Field>
      <Field label="What would you like to build or learn?" htmlFor="ai-goals">
        <Textarea id="ai-goals" rows={3} value={f.goals} onChange={set("goals")} data-testid="ai-lead-goals" />
      </Field>
      <AddOnPicker value={f.add_ons} other={f.add_ons_other} onChange={set("add_ons")} onOtherChange={set("add_ons_other")} testId="ai-lead-addons" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Your Name" htmlFor="ai-name"><Input id="ai-name" required value={f.name} onChange={set("name")} data-testid="ai-lead-name" /></Field>
        <Field label="Email" htmlFor="ai-email"><Input id="ai-email" type="email" required value={f.email} onChange={set("email")} data-testid="ai-lead-email" /></Field>
      </div>
      <Field label="WhatsApp number" htmlFor="ai-wa" hint="Include country code, e.g. +505 ...">
        <Input id="ai-wa" type="tel" required pattern="[0-9+\s\(\)\.\-]{7,}" title="Enter your WhatsApp number with country code" value={f.whatsapp} onChange={set("whatsapp")} data-testid="ai-lead-whatsapp" />
      </Field>
      {f.whatsapp && (
        <CheckRow checked={f.whatsapp_consent} onChange={set("whatsapp_consent")} testId="ai-lead-whatsapp-consent">I agree that Mr. Greg may contact me on WhatsApp.</CheckRow>
      )}
      <Field label="Preferred way to be contacted">
        <ChipGroup options={CONTACT_PREFS} value={f.contact_pref} onChange={set("contact_pref")} multi={false} testId="ai-lead-contact-pref" />
      </Field>
      <button disabled={busy} className="self-start inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-7 py-3.5 font-semibold shadow-md hover:bg-[#D96F28] disabled:opacity-60" data-testid="ai-lead-submit-btn">
        {busy ? "Sending..." : "Get more information"} <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  );
}
